/**
 * 69.52 step 1 (ARCHITECTURE §5.27; DECISIONS 261, 262 (a), the owner: "route around"): a ship's route round the air defence its
 * side knows of. Known cover is the enemy's point defence the side has seen (core/mountIntel.ts knownMounts: the mounts and
 * 69.50's air-defence sites), each a disc of its reach (mountReach) and MOUNT_INTEL.marginPeople. At the call the straight track
 * from the ship's origin to its landing is tested against each disc; one it crosses (neither end inside it: the landing's own
 * move, 69.9 step 5, keeps the landing out) gets a turning point past its edge on the side the track passes, and the new
 * legs are tested again, up to SKY_ROUTE.maxTurns. The route is the great circles between the points (core/dropship.ts: the
 * pose still a pure function of the tick). `GameConfig.skyRule`: `route` (the default) bends; `refuse` refuses a call whose
 * straight track crosses known cover; `fly` flies it straight. A call's `hot` flies it straight whatever the rule. Pure.
 */
import type { GameState, Vec3 } from '../types';
import { MOUNT_INTEL, SKY_ROUTE, SKY_RISK, SKY_BENDS, SKY_EVADE } from '../constants/action';
import { PHYS_DT } from '../constants';
import { knownMounts, mountReach } from './mountIntel';
import { along, lineFrom } from './airstrike';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dacos, dasin, dsin, dcos, datan2 } from './dmath';
import { FLIGHT_GRAVITY } from '../constants/flightModel';
import { vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';

export type SkyRule = 'route' | 'refuse' | 'fly';
/** The side's rule (262 (a): `route` unless set). */
export const skyRuleOf = (config: Pick<GameState['config'], 'skyRule'>): SkyRule => config.skyRule ?? 'route';

/** A known cover disc: its centre (a unit direction) and its angular radius. */
export interface CoverDisc { c: Vec3; rho: number }

/** 69.52 step 2: a pilot's risk (0–1): a hot call's 1, else its side's (GameConfig.pilotRisk; 262 (b): cautious unless set). */
export function pilotRisk(config: Pick<GameState['config'], 'pilotRisk'>, team: number, hot = false): number {
  return hot ? 1 : Math.max(0, Math.min(1, config.pilotRisk?.[team] ?? SKY_RISK.cautious));
}
/** 69.52 step 2: the berth a route keeps past known cover's reach at `risk` (people): the full berth at 0, none at 1. */
export const riskMargin = (risk: number): number => (1 - risk) * (MOUNT_INTEL.marginPeople + SKY_RISK.widePeople);
/** 69.52 step 2, 69.66: the bends' swing (people; GameConfig.shipWeave, else SKY_BENDS's; 0: none). */
export const weaveAmplitude = (config: Pick<GameState['config'], 'shipWeave'>): number => Math.max(0, config.shipWeave ?? SKY_BENDS.swingPeople);

/**
 * The cover `team` knows of on `planetIndex`: each known enemy mount's reach, the berth at `risk` (riskMargin) and the
 * bends' swing (so a ship off its line in a bend stays out), as an angle.
 */
export function coverDiscs(state: GameState, team: number, planetIndex: number, risk = SKY_RISK.cautious): CoverDisc[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, past = riskMargin(risk) + weaveAmplitude(state.config);
  return knownMounts(state, team).filter(m => m.planetIndex === planetIndex)
    .map(m => ({ c: vecNormalize(m.surfaceDir), rho: ((mountReach(m) + past) * person) / planet.radius }));
}

/** 69.62 (§5.31: a rescue flies through known gun mounts, never under a known site): the reach of the air-defence sites (69.50) `team` knows of on a planet, MOUNT_INTEL.marginPeople past; `guns`: of its gun mounts (C-RAM, CIWS) instead. */
export function siteDiscs(state: GameState, team: number, planetIndex: number, guns = false): CoverDisc[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  return knownMounts(state, team).filter(m => m.planetIndex === planetIndex && (m.pd?.variant === 'site') !== guns)
    .map(m => ({ c: vecNormalize(m.surfaceDir), rho: ((mountReach(m) + MOUNT_INTEL.marginPeople) * person) / planet.radius }));
}
/** 69.62: is `dir` inside any of `discs`? */
export const inCover = (dir: Vec3, discs: readonly CoverDisc[]): boolean => {
  const d = vecNormalize(dir);
  return discs.some(k => dacos(Math.max(-1, Math.min(1, vecDot(k.c, d)))) <= k.rho);
};

/** 69.66: a knot of a leg's bends — `d` along it and `o` across (people, + to its right), its corner `w` wide. */
interface Knot { d: number; o: number; w: number }
/** 69.66: a seeded share (0–1) for flight `id`'s `k`th draw (integer hashing: the same in every engine). */
function share(id: number, k: number): number {
  let h = Math.imul((id | 0) ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(k + 1, 0xc2b2ae35);
  h ^= h >>> 13; h = Math.imul(h, 0x27d4eb2f); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * 69.66: the knots of flight `id`'s bends along a leg `Lp` people long at swing `amp` (people): straight off its start, one
 * or two bends, and on the way in (`approach`) the off-axis last stretch and the hard turn onto the landing; level (0) before
 * the first and after the last. Null: none (a swing of 0, or a leg too short).
 */
function bendKnots(id: number, Lp: number, amp: number, approach: boolean): Knot[] | null {
  const B = SKY_BENDS, a = B.startPeople;
  if (!(amp > 0) || !(Lp > 2 * a + B.minRunPeople / 2)) return null;
  let sign = share(id, 0) < 0.5 ? 1 : -1;
  // the last stretch in: `finalShare` of the swing aside at θ to the axis, closing to the landing through the hard turn
  const th = ((B.finalDegrees[0] + share(id, 1) * (B.finalDegrees[1] - B.finalDegrees[0])) * Math.PI) / 180, slope = dsin(th) / dcos(th);
  const oF = B.finalShare * amp, dF = Lp - B.turnPeople / 2 - oF / slope;
  const final = approach && dF - a >= B.minRunPeople / 2;
  const end = final ? dF : Lp - a, span = end - a;
  const n = span >= 3 * B.minRunPeople ? (share(id, 2) < 0.5 ? 1 : 2) : span >= 2 * B.minRunPeople ? 1 : 0;
  const at = n === 1 ? [0.35 + 0.3 * share(id, 3)] : n === 2 ? [0.22 + 0.16 * share(id, 3), 0.62 + 0.16 * share(id, 4)] : [];
  const K: Knot[] = [{ d: a, o: 0, w: 2 * a }];
  at.forEach((x, i) => { K.push({ d: a + span * x, o: sign * amp * (0.55 + 0.45 * share(id, 5 + i)), w: B.cornerPeople }); sign = -sign; });
  if (final) K.push({ d: dF, o: sign * oF, w: B.cornerPeople }, { d: Lp - B.turnPeople / 2, o: 0, w: B.turnPeople });
  else K.push({ d: Lp - a, o: 0, w: 2 * a });
  if (K.length < 3) return null;
  // (each corner within its runs, a straight left between two — the bank level a moment, not flipped: the first's starts at the
  // leg's start, the last's ends at its end)
  for (let i = 0; i < K.length; i++) K[i].w = Math.min(K[i].w, i > 0 ? 0.7 * (K[i].d - K[i - 1].d) : 2 * K[i].d, i < K.length - 1 ? 0.7 * (K[i + 1].d - K[i].d) : 2 * (Lp - K[i].d));
  return K;
}

/**
 * 69.66 (replacing 69.52 step 2's weave): flight `id`'s bends at `d` world units along a leg `L` long (the swing `amp` people,
 * the person's size in world units; `approach`: the way in, with its last stretch and its turn onto the landing) — its
 * offset across the track (world units, + to its right), its slope (the offset's rate along it) and its curvature (the
 * slope's rate, per world unit: + turning right). Each corner a smoothstep in the slope, the turn rate rising and falling.
 * Zero with `amp` 0. Pure.
 */
export function weaveAt(id: number, d: number, L: number, amp: number, person: number, approach = false): { off: number; slope: number; curve: number } {
  return bendsOf(id, L, amp, person, approach)(d);
}

const NO_BEND = { off: 0, slope: 0, curve: 0 };
const noBends = (): { off: number; slope: number; curve: number } => NO_BEND;
/**
 * 69.66: weaveAt for one leg, its knots found once — `(d) => weaveAt(id, d, L, amp, person, approach)` — for a caller that reads
 * many places along it (core/dropship.ts routeCruise: the ground under every sample; lane H's tick-spike note found a gunship's
 * call at 8.6 ms with the knots remade per sample).
 */
export function bendsOf(id: number, L: number, amp: number, person: number, approach = false): (d: number) => { off: number; slope: number; curve: number } {
  if (!(amp > 0) || !(L > 0) || !(person > 0)) return noBends;
  const K = bendKnots(id, L / person, amp, approach);
  if (!K) return noBends;
  const M = K.map((k, i) => (i >= K.length - 1 ? 0 : (K[i + 1].o - k.o) / (K[i + 1].d - k.d)));   // (the run after knot i)
  const m = (i: number): number => (i < 0 ? 0 : M[i]);
  return (d: number) => {
    const x = d / person;
    for (let i = 0; i < K.length; i++) {
      const k = K[i], s0 = k.d - k.w / 2;
      if (x < s0) return i === 0 ? NO_BEND : { off: (K[i - 1].o + m(i - 1) * (x - K[i - 1].d)) * person, slope: m(i - 1), curve: 0 };
      if (x <= k.d + k.w / 2 && k.w > 0) {
        const m0 = m(i - 1), dm = m(i) - m0, u = (x - s0) / k.w;
        return { off: (k.o - (m0 * k.w) / 2 + m0 * (x - s0) + dm * k.w * (u * u * u - (u * u * u * u) / 2)) * person, slope: m0 + dm * u * u * (3 - 2 * u), curve: (dm * 6 * u * (1 - u)) / (k.w * person) };
      }
    }
    return NO_BEND;
  };
}

/** 69.66: the bank (radians, the left wing down) of a turn of `curve` per world unit flown at `speed` people a second. */
export function bankOf(curve: number, speed: number, person: number): number {
  const max = (SKY_BENDS.bankMaxDegrees * Math.PI) / 180, b = -datan2(speed * speed * curve * person, FLIGHT_GRAVITY);
  return Math.max(-max, Math.min(max, b));
}

const angle = (a: Vec3, b: Vec3): number => dacos(Math.max(-1, Math.min(1, vecDot(a, b))));

/** The angle from `c` to the great-circle segment `a`–`b` (unit directions), and the segment's point nearest it. */
export function segmentNearest(a: Vec3, b: Vec3, c: Vec3): { d: number; p: Vec3 } {
  const ab = angle(a, b), n0 = vecCross(a, b);
  if (ab < 1e-9 || vecLength(n0) < 1e-12) return { d: angle(a, c), p: a };
  const n = vecNormalize(n0), off = vecDot(c, n), onCircle = vecSub(c, vecScale(n, off));
  if (vecLength(onCircle) > 1e-12) {
    const p = vecNormalize(onCircle);
    if (Math.abs(angle(a, p) + angle(p, b) - ab) < 1e-9) return { d: dasin(Math.min(1, Math.abs(off))), p };
  }
  const da = angle(a, c), db = angle(b, c);
  return da <= db ? { d: da, p: a } : { d: db, p: b };
}

/** Does the segment `a`–`b` cross disc `k` — come within its radius while neither end is inside it? */
function crosses(a: Vec3, b: Vec3, k: CoverDisc): boolean {
  if (angle(a, k.c) <= k.rho || angle(b, k.c) <= k.rho) return false;
  return segmentNearest(a, b, k.c).d < k.rho;
}

/** Does the straight track from `origin` to `land` cross any of `discs`? */
export function trackCrossesCover(origin: Vec3, land: Vec3, discs: readonly CoverDisc[]): boolean {
  const a = vecNormalize(origin), b = vecNormalize(land);
  return discs.some(k => crosses(a, b, k));
}

/**
 * The turning points of a route from `origin` to `land` round `discs` (unit directions, in order; empty: the straight track
 * is clear). Each pass bends the first leg that crosses a disc at a point past the disc's edge (SKY_ROUTE.clearance of its
 * radius) on the side the leg passes it (a leg straight through its centre goes round its left); `clear` false when the
 * passes ran out with a leg still crossing (the route as far as it got).
 */
export function routeAround(origin: Vec3, land: Vec3, discs: readonly CoverDisc[], radius: number): { via: Vec3[]; clear: boolean } {
  const pts = [vecNormalize(origin), vecNormalize(land)];
  for (let turn = 0; turn < SKY_ROUTE.maxTurns; turn++) {
    let hit: { i: number; k: CoverDisc } | null = null;
    for (let i = 0; i + 1 < pts.length && !hit; i++) for (const k of discs) if (crosses(pts[i], pts[i + 1], k)) { hit = { i, k }; break; }
    if (!hit) return { via: pts.slice(1, -1), clear: true };
    const { i, k } = hit, a = pts[i], b = pts[i + 1], { p } = segmentNearest(a, b, k.c);
    // the side the leg passes the centre on: from the centre toward its nearest point; through the centre, the leg's left
    const side = angle(p, k.c) > 1e-6 ? vecScale(lineFrom(k.c, p), -1) : vecNormalize(vecCross(k.c, vecSub(b, a)));
    pts.splice(i + 1, 0, along(k.c, side, k.rho * SKY_ROUTE.clearance * radius, radius));
  }
  const clear = !pts.some((_, i) => i + 1 < pts.length && discs.some(k => crosses(pts[i], pts[i + 1], k)));
  return { via: pts.slice(1, -1), clear };
}

/** 69.52 step 3: a manoeuvre's seconds (a break-off's is the rest of the flight: core/skyEvade.ts). */
export const evadeSeconds = (kind: 'jink' | 'low' | 'break'): number => (kind === 'jink' ? SKY_EVADE.jinkSeconds : kind === 'low' ? SKY_EVADE.lowSeconds : Infinity);

/**
 * 69.52 step 3: a manoeuvre's terms at `tick` for the pose (core/dropship.ts pathPose; core/skyEvade.ts starts them): a jink's
 * swing across the track (world units, + to its right) and its rate (world units a second, for the heading), and a drop
 * low's weight (0–1: how far toward the nap height). Zero outside the manoeuvre, eased in and out.
 */
export function evadeTerms(f: { evade?: { kind: 'jink' | 'low' | 'break'; tick: number } }, tick: number, person: number): { swing: number; rate: number; low: number } {
  const e = f.evade;
  if (!e || e.kind === 'break') return { swing: 0, rate: 0, low: 0 };
  const t = (tick - e.tick) * PHYS_DT, T = evadeSeconds(e.kind);
  if (t < 0 || t > T) return { swing: 0, rate: 0, low: 0 };
  const ramp = SKY_EVADE.easeSeconds, x = Math.min(1, t / ramp, (T - t) / ramp), w = x * x * (3 - 2 * x);
  if (e.kind !== 'jink') return { swing: 0, rate: 0, low: w };
  const om = (2 * Math.PI) / SKY_EVADE.jinkPeriodSeconds, a = w * SKY_EVADE.jinkPeople * person;
  return { swing: a * dsin(om * t), rate: a * om * dcos(om * t), low: 0 };
}
